// src/pages/CustomerDetailPage.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { customersAPI } from '../services/api';

const CustomerDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchCustomer = async () => {
      try {
        console.log('Versuche Kundendaten zu laden, ID:', id);
        const response = await customersAPI.getById(id);
        console.log('Kundendaten erfolgreich geladen:', response.data);
        setCustomer(response.data.customer);
        setError('');
      } catch (err) {
        console.error('Fehler beim Laden der Kundendaten:', err);
        
        // Prüfen, ob es ein Authentifizierungsfehler ist
        if (err.response && err.response.status === 401) {
          // Manuell zur Login-Seite umleiten, wenn nicht authentifiziert
          console.log('Nicht authentifiziert, leite zur Login-Seite um');
          navigate('/login');
          return; // Wichtig: Return, um weitere Verarbeitung zu stoppen
        }
        
        setError('Fehler beim Laden der Kundendaten');
      } finally {
        setLoading(false);
      }
    };

    fetchCustomer();
  }, [id, navigate]);

  if (loading) {
    return <div className="text-center py-6">Lade Kundendaten...</div>;
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
          <p className="text-red-700">{error}</p>
        </div>
        <Link to="/kunden" className="text-primary-600 hover:text-primary-900">
          Zurück zur Kundenliste
        </Link>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="p-4">
        <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-6">
          <p className="text-yellow-700">Kunde nicht gefunden</p>
        </div>
        <Link to="/kunden" className="text-primary-600 hover:text-primary-900">
          Zurück zur Kundenliste
        </Link>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-0">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Kundendetails</h1>
        <div className="space-x-3">
          <Link
            to={`/kunden/${id}/edit`}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded"
          >
            Bearbeiten
          </Link>
          <Link
            to="/kunden"
            className="border border-gray-300 px-4 py-2 rounded text-gray-700 hover:bg-gray-100"
          >
            Zurück
          </Link>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h2 className="text-xl font-medium mb-4">Kontaktinformationen</h2>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm font-medium text-gray-500">Name</dt>
                <dd className="mt-1 text-gray-900">{customer.first_name} {customer.last_name}</dd>
              </div>
              {customer.company_name && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Firma</dt>
                  <dd className="mt-1 text-gray-900">{customer.company_name}</dd>
                </div>
              )}
              {customer.additional_address && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Zusätzliche Adresse</dt>
                  <dd className="mt-1 text-gray-900">{customer.additional_address}</dd>
                </div>
              )}
              {customer.email && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">E-Mail</dt>
                  <dd className="mt-1 text-gray-900">
                    <a href={`mailto:${customer.email}`} className="text-primary-600 hover:text-primary-900">
                      {customer.email}
                    </a>
                  </dd>
                </div>
              )}
              {customer.phone && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Telefon</dt>
                  <dd className="mt-1 text-gray-900">
                    <a href={`tel:${customer.phone}`} className="text-primary-600 hover:text-primary-900">
                      {customer.phone}
                    </a>
                  </dd>
                </div>
              )}
            </dl>
          </div>

          <div>
            <h2 className="text-xl font-medium mb-4">Adresse</h2>
            <dl className="space-y-3">
              {customer.address && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Straße</dt>
                  <dd className="mt-1 text-gray-900">{customer.address}</dd>
                </div>
              )}
              {(customer.postal_code || customer.city) && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">PLZ / Ort</dt>
                  <dd className="mt-1 text-gray-900">{customer.postal_code} {customer.city}</dd>
                </div>
              )}
            </dl>
          </div>
        </div>

        {customer.notes && (
          <div className="mt-6">
            <h2 className="text-xl font-medium mb-3">Notizen</h2>
            <div className="bg-gray-50 p-4 rounded border border-gray-200">
              <p className="text-gray-700 whitespace-pre-line">{customer.notes}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerDetailPage;
